<template>
  <div
    class="rounded-3xl p-6 md:p-8 bg-gradient-to-br from-[#1c1f26] to-[#181b21] border border-red-500/10 shadow-[0_0_45px_rgba(0,0,0,0.65)]"
  >
    <div class="flex items-center justify-between">
      <p class="text-sm text-gray-400">
        Pergunta <span class="text-white font-semibold">{{ index + 1 }}</span
        >/<span>{{ total }}</span>
      </p>

      <span
        class="text-xs px-2 py-1 rounded-full border border-white/10 bg-white/5 text-gray-300"
      >
        {{ questionnaireName }}
      </span>
    </div>

    <Transition name="qslide" mode="out-in">
      <div :key="index" class="mt-6">
        <div class="flex items-start gap-4 text-start">
          <div
            class="shrink-0 w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white font-semibold"
          >
            {{ index + 1 }}
          </div>

          <p class="text-lg md:text-xl text-white leading-snug">
            {{ question }}
          </p>
        </div>

        <div
          class="mt-8 grid gap-4"
          :class="
            normalizedOptions.length <= 2
              ? 'grid-cols-2'
              : 'grid grid-cols-1 lg:grid-cols-3'
          "
        >
          <!-- : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-5' -->
          <button
            v-for="option in normalizedOptions"
            :key="option.value"
            type="button"
            class="rounded-2xl font-semibold transition border text-left px-4 py-4"
            :class="optionClass(option)"
            @click="$emit('answer', option.value)"
          >
            <div class="flex items-start gap-3">
              <span
                class="shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold border my-auto"
                :class="optionBadgeClass(option)"
              >
                {{ option.value }}
              </span>

              <div class="min-w-0 my-auto">
                <p class="text-sm md:text-base font-semibold">
                  {{ option.label }}
                </p>

                <!-- <p
                  v-if="responseMode === 'binary'"
                  class="mt-1 text-xs opacity-80"
                >
                  Toque para responder
                </p> -->
              </div>
            </div>
          </button>
        </div>

        <p class="mt-4 text-xs text-gray-500">
          {{
            responseMode === "binary"
              ? "Toque em NÃO ou SIM para avançar automaticamente."
              : "Toque em uma das opções para avançar automaticamente."
          }}
        </p>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from "vue";

const props = defineProps({
  index: { type: Number, required: true },
  total: { type: Number, required: true },
  question: { type: String, required: true },

  answerValue: {
    type: Number,
    default: null,
  },

  responseMode: {
    type: String,
    default: "binary",
  },

  options: {
    type: Array,
    default: () => [],
  },

  questionnaireName: {
    type: String,
    default: "Questionário",
  },
});

defineEmits(["answer"]);

const binaryOptions = [
  { value: 0, label: "NÃO" },
  { value: 1, label: "SIM" },
];

const scaleOptions = [
  { value: 1, label: "Nunca/quase nunca" },
  { value: 2, label: "Raramente" },
  { value: 3, label: "Às vezes" },
  { value: 4, label: "Frequentemente" },
  { value: 5, label: "Sempre" },
];

const srq21LastQuestionOptions = [
  { value: 1, label: "Muito ruim" },
  { value: 2, label: "Ruim" },
  { value: 3, label: "Boa" },
  { value: 4, label: "Muito boa" },
  { value: 5, label: "Excelente" },
];

const isLastSrq21Question = computed(() => {
  return (
    Number(props.index) + 1 === 41 &&
    String(props.questionnaireName || "")
      .toLowerCase()
      .includes("srq-21")
  );
});

function normalizeOption(option) {
  return {
    ...option,
    value: Number(option.value),
    label: String(option.label || "").trim(),
  };
}

function removeDuplicateOptions(options) {
  const map = new Map();

  options.forEach((option) => {
    const normalizedOption = normalizeOption(option);

    if (!map.has(normalizedOption.value)) {
      map.set(normalizedOption.value, normalizedOption);
    }
  });

  return Array.from(map.values()).sort((a, b) => a.value - b.value);
}

const normalizedOptions = computed(() => {
  if (props.responseMode === "binary") {
    return binaryOptions;
  }

  if (isLastSrq21Question.value) {
    return srq21LastQuestionOptions;
  }

  if (Array.isArray(props.options) && props.options.length) {
    return removeDuplicateOptions(props.options);
  }

  return scaleOptions;
});

function optionClass(option) {
  const active = Number(props.answerValue) === Number(option.value);

  if (props.responseMode === "binary") {
    const isPositive = Number(option.value) === 1;

    if (active && isPositive) {
      return "bg-green-500 text-white border-green-500 shadow-[0_0_30px_rgba(34,197,94,0.25)]";
    }

    if (active && !isPositive) {
      return "bg-red-500 text-white border-red-500 shadow-[0_0_30px_rgba(239,68,68,0.25)]";
    }

    return "bg-white/5 text-gray-200 border-white/10 hover:bg-white/10";
  }

  return active
    ? "bg-green-400/60 text-white border-green-500 shadow-[0_0_30px_rgba(34,197,94,0.25)]"
    : "bg-white/5 text-gray-200 border-white/10 hover:bg-white/10";
}

function optionBadgeClass(option) {
  const active = Number(props.answerValue) === Number(option.value);

  if (props.responseMode === "binary") {
    if (active && Number(option.value) === 1) {
      return "border-green-400/60 bg-green-500/20 text-green-100";
    }

    if (active && Number(option.value) === 0) {
      return "border-red-400/60 bg-red-500/20 text-red-100";
    }

    return "border-white/10 bg-white/5 text-gray-300";
  }

  return active
    ? "border-green-500 bg-green-500/20 text-green-100"
    : "border-white/10 bg-white/5 text-gray-300";
}
</script>

<style scoped>
.qslide-enter-from,
.qslide-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
.qslide-enter-active,
.qslide-leave-active {
  transition: all 0.22s ease;
}
</style>
